import { Link } from "react-router-dom";
import { Check, Shield, Mail, ArrowRight } from "lucide-react";
import PayPalSubscribeButton from "@/components/PayPalSubscribeButton";

/**
 * Public pricing page — visible to logged-out visitors. Subscribe buttons
 * are rendered by the PayPal SDK (Subscriptions API) and load the customer
 * into PayPal's hosted checkout without leaving the page. Lives at /pricing.
 */
export default function Pricing() {
  return (
    <div className="min-h-screen bg-stone-50">
      <Header />
      <main className="max-w-6xl mx-auto px-6 py-16">
        <div className="text-center max-w-2xl mx-auto">
          <div className="text-xs uppercase tracking-[0.25em] font-semibold text-emerald-700">Pricing</div>
          <h1 className="mt-3 font-display text-5xl font-medium text-stone-900 tracking-tight">Simple, monthly pricing</h1>
          <p className="mt-4 text-stone-600 leading-relaxed">
            One plan per center. No setup fees, no per-child fees. Cancel any time.
          </p>
        </div>

        <div className="mt-14 grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          <PriceCard
            name="Starter"
            price="$299"
            sub="per month · 1 center"
            tier="starter"
            planId={process.env.REACT_APP_PAYPAL_STARTER_PLAN_ID}
            features={[
              "Up to 60 enrolled children",
              "Digital enrollment & records",
              "All DCY forms (01208, 01228, 01229, 01305 …)",
              "Parent + staff apps",
              "GPS clock-in & ratio alerts",
              "Compliance nightly reports",
              "Email support",
            ]}
          />
          <PriceCard
            name="Growth"
            price="$499"
            sub="per month · 1 center"
            highlight
            tier="growth"
            planId={process.env.REACT_APP_PAYPAL_GROWTH_PLAN_ID}
            features={[
              "Unlimited enrolled children",
              "Everything in Starter, plus:",
              "Multi-classroom dashboards",
              "Auto classroom transitions (DCY 01229)",
              "Lesson plans aligned to Ohio ELDS",
              "Monthly compliance digest",
              "Priority email + chat support",
              "Dedicated onboarding session",
            ]}
          />
        </div>

        <div className="mt-12 rounded-2xl bg-emerald-50 border border-emerald-200 p-8 text-center">
          <h2 className="font-display text-2xl font-medium text-emerald-900">Running multiple centers?</h2>
          <p className="mt-2 text-sm text-emerald-800">We offer custom multi-location pricing. Email us and we&apos;ll put together a quote.</p>
          <a href="mailto:info@childcheckoh.com" className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-emerald-700 hover:bg-emerald-800 text-white font-semibold">
            <Mail className="w-4 h-4" /> info@childcheckoh.com
          </a>
        </div>

        <div className="mt-12 text-center text-sm text-stone-600 space-y-2">
          <p><b>All plans include:</b> SSL-encrypted data, daily backups, DCY-compliant document templates, parent/staff mobile-ready apps, and free updates.</p>
          <p><b>Payment:</b> Secured by PayPal. Accepts your PayPal balance, all major credit / debit cards, and your linked bank account. Cancel from your PayPal account at any time.</p>
          <p><b>Billing cycle:</b> Charged on the same day each month. Cancel any time from your dashboard — no refunds for partial months (see <Link to="/refund" className="text-emerald-700 underline">Refund Policy</Link>).</p>
        </div>
      </main>
      <Footer />
    </div>
  );
}

function PriceCard({ name, price, sub, features, highlight, tier, planId }) {
  return (
    <div className={`rounded-2xl border p-7 bg-white flex flex-col ${highlight ? "border-emerald-600 ring-2 ring-emerald-600/20 shadow-lg" : "border-stone-200"}`}>
      {highlight && <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-700 mb-2">Most popular</div>}
      <h3 className="font-display text-2xl font-medium text-stone-900">{name}</h3>
      <div className="mt-3 flex items-baseline gap-2">
        <span className="text-4xl font-bold text-stone-900">{price}</span>
        <span className="text-sm text-stone-500">USD</span>
      </div>
      <div className="text-xs text-stone-500 mt-1">{sub}</div>
      <ul className="mt-6 space-y-3 flex-1">
        {features.map((f, i) => (
          <li key={i} className="flex gap-2 text-sm text-stone-700"><Check className="w-4 h-4 text-emerald-700 shrink-0 mt-0.5" /> {f}</li>
        ))}
      </ul>
      <div className="mt-7">
        <PayPalSubscribeButton planId={planId} tier={tier} />
        <Link to="/login" className="mt-3 block text-center text-xs text-stone-500 hover:text-stone-800 underline">
          Already a customer? Sign in
        </Link>
      </div>
    </div>
  );
}

export function Header() {
  return (
    <header className="border-b border-stone-200 bg-white">
      <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3">
          <img src="/logo-256.png" alt="ChildCheckOh" className="w-9 h-9 rounded-lg" />
          <span className="font-display text-lg font-semibold text-stone-900">ChildCheck<span className="text-emerald-700">Oh</span></span>
        </Link>
        <nav className="flex items-center gap-5 text-sm text-stone-600">
          <Link to="/pricing" className="hover:text-stone-900">Pricing</Link>
          <Link to="/login" className="px-3 py-1.5 rounded-lg bg-emerald-700 hover:bg-emerald-800 text-white">Sign in</Link>
        </nav>
      </div>
    </header>
  );
}

export function Footer() {
  return (
    <footer className="border-t border-stone-200 bg-white mt-12">
      <div className="max-w-6xl mx-auto px-6 py-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 text-sm text-stone-600">
        <div className="flex items-center gap-3">
          <img src="/logo-256.png" alt="ChildCheckOh" className="w-8 h-8 rounded-lg" />
          <div>
            <div className="font-semibold text-stone-900">ChildCheckOh</div>
            <div className="text-xs text-stone-500">Ohio childcare operations · DCY compliance</div>
          </div>
        </div>
        <nav className="flex flex-wrap gap-x-5 gap-y-2 text-xs">
          <Link to="/pricing" className="hover:text-stone-900">Pricing</Link>
          <Link to="/terms" className="hover:text-stone-900">Terms of Service</Link>
          <Link to="/privacy" className="hover:text-stone-900">Privacy Policy</Link>
          <Link to="/refund" className="hover:text-stone-900">Refund Policy</Link>
          <a href="mailto:info@childcheckoh.com" className="hover:text-stone-900">Contact</a>
        </nav>
      </div>
      <div className="border-t border-stone-100 py-4 text-center text-[11px] text-stone-500">
        © {new Date().getFullYear()} ChildCheckOh · childcheckoh.com · info@childcheckoh.com · Payments processed by PayPal
      </div>
    </footer>
  );
}
